import _ from 'lodash';
import { CampaignConstants } from '../../../constants/CampaignConstants';
import { GB, MB } from '../../../constants/Units';
import { determineLabelForMonths, getLabelsByLanguage, produceValidMBValue } from '../../../utilites/util';
import {
  IDigitalService,
  ISoftOfferBenefit,
  ISoftOfferBenefits,
  ISoftOfferPackage,
  ISoftOfferPackageData,
  OfferSpecProps,
} from '../types';

export type SoftOfferSpecType = 'data' | 'phone' | 'sms' | 'phone&sms';

export default class SoftOfferPackage implements ISoftOfferPackage {

  id: number;
  cosmosSubscription: number;
  dsNumber: number;
  maxDsNumber: boolean;
  img: string;
  sms: number | string;
  min: number | string;
  mint: number | string;
  minr: number | string;
  data: ISoftOfferPackageData;
  datar: ISoftOfferPackageData;
  rzb: ISoftOfferPackageData;
  rzbm: number | string;
  name: string;
  priority: number;
  subscription: number;
  tmCode: number;
  url: string;
  benefits: ISoftOfferBenefits;
  youth: number;
  smartic: number;
  home: number;
  drivey: number;
  services: IDigitalService[];
  infoUrl: string;
  recommended?: boolean;
  usage?: string;
  actualSubscription?: number;

  constructor(
    id: number,
    cosmosSubscription: number,
    dsNumber: number,
    maxDsNumber: boolean,
    img: string,
    sms: number | string,
    min: number | string,
    mint: number | string,
    minr: number | string,
    data: ISoftOfferPackageData,
    datar: ISoftOfferPackageData,
    rzb: ISoftOfferPackageData,
    rzbm: number | string,
    name: string,
    priority: number,
    subscription: number,
    tmCode: number,
    url: string,
    benefits: ISoftOfferBenefits,
    youth: number,
    smartic: number,
    home: number,
    drivey: number,
    services: IDigitalService[],
    infoUrl: string,
    recommended: boolean | undefined,
    usage: string | undefined,
  ) {
    this.id = id;
    this.cosmosSubscription = cosmosSubscription;
    this.dsNumber = dsNumber;
    this.maxDsNumber = maxDsNumber;
    this.img = img;
    this.sms = sms;
    this.min = min;
    this.mint = mint;
    this.minr = minr;
    this.data = data;
    this.datar = datar;
    this.rzb = rzb;
    this.rzbm = rzbm;
    this.name = name;
    this.priority = priority;
    this.subscription = subscription;
    this.tmCode = tmCode;
    this.url = url;
    this.benefits = benefits;
    this.youth = youth;
    this.smartic = smartic;
    this.home = home;
    this.drivey = drivey;
    this.services = services;
    this.infoUrl = infoUrl;
    this.recommended = recommended;
    this.usage = usage;
  }

  public isUnlimitedAll(): boolean {
    return (
      (this.isMinUnlimited() && this.isMintUnlimited() && this.isSmsUnlimited()) ||
      (this.isMinUnlimited() && this.isSmsUnlimited() && !this.mint)
    );
  }

  public isMinUnlimited(): boolean {
    return this.min === CampaignConstants.unlimitedIdentifier;
  }

  public isMintUnlimited(): boolean {
    return this.mint === CampaignConstants.unlimitedIdentifier;
  }

  public isSmsUnlimited(): boolean {
    return this.sms === CampaignConstants.unlimitedIdentifier;
  }

  public isDataUnlimited(): boolean {
    return this.data.unlimited || this.hasAdditionalUnlimitedData();
  }

  public renderDataValue(): string {
    return this.renderData(this.data.mbs);
  }

  public renderData(mbs: number): string {
    if (!mbs) {
      return `-`;
    }
    if (mbs > 999) {
      return `${mbs / 1000} GB`;
    }
    return `${mbs} MB`;
  }

  public hasAdditionalLimitedData(): boolean {
    const dataDefaultBenefit = this.getDataBenefit(CampaignConstants.benefit.name.DEFAULT);
    const dataPromoBenefit = this.getDataBenefit(CampaignConstants.benefit.name.PROMO);
    if (
      dataDefaultBenefit &&
      _.has(dataDefaultBenefit, 'calculated') &&
      typeof dataDefaultBenefit.calculated === 'number'
    ) {
      return true;
    }
    if (dataPromoBenefit && _.has(dataPromoBenefit, 'calculated') && typeof dataPromoBenefit.calculated === 'number') {
      return true;
    }
    return false;
  }

  public hasAdditionalUnlimitedData(): boolean {
    const dataDefaultBenefit = this.getDataBenefit(CampaignConstants.benefit.name.DEFAULT);
    const dataPromoBenefit = this.getDataBenefit(CampaignConstants.benefit.name.PROMO);
    if (
      dataDefaultBenefit &&
      _.has(dataDefaultBenefit, 'calculated.value') &&
      dataDefaultBenefit.calculated.value === CampaignConstants.benefit.UNLIMITED_CONSTANT_FROM_CAMPAIGN
    ) {
      return true;
    }
    if (
      dataPromoBenefit &&
      _.has(dataPromoBenefit, 'calculated.value') &&
      dataPromoBenefit.calculated.value === CampaignConstants.benefit.UNLIMITED_CONSTANT_FROM_CAMPAIGN
    ) {
      return true;
    }
    return false;
  }

  public hasAdditionalTimeLimitedData(): boolean {
    const timeLimitedData = this.getAdditionalTimeLimitedData();
    return _.has(timeLimitedData, 'calculated');
  }

  public getAdditionalTimeLimitedData(): ISoftOfferBenefit | null {
    // Note: By the campaign business logic, there is no chance to they set timeLimitedBenefit for both benefit and promoBenefit.
    // So if wee find time-limited benefit in one place we will terminate the searching process
    const dataDefaultComplexBenefit = this.getDataBenefit(
      CampaignConstants.benefit.name.DEFAULT,
      CampaignConstants.benefit.type.COMPLEX
    );
    //Note: && dataDefaultComplexBenefit.calculated.value === CampaignConstants.benefit.UNLIMITED_CONSTANT_FROM_CAMPAIGN => this is additional check to be sure that campaign
    // doesn`t have limited data for specific time period (additional protection from bad campaign configuration)
    if (
      dataDefaultComplexBenefit &&
      dataDefaultComplexBenefit.calculated.value === CampaignConstants.benefit.UNLIMITED_CONSTANT_FROM_CAMPAIGN
    ) {
      return dataDefaultComplexBenefit;
    }
    const dataPromoComplexBenefit = this.getDataBenefit(
      CampaignConstants.benefit.name.PROMO,
      CampaignConstants.benefit.type.COMPLEX
    );
    if (
      dataPromoComplexBenefit &&
      dataPromoComplexBenefit.calculated.value === CampaignConstants.benefit.UNLIMITED_CONSTANT_FROM_CAMPAIGN
    ) {
      return dataPromoComplexBenefit;
    }
    return null;
  }

  public renderAdditionalTimeLimitedDataPeriod(): string {
    const additionalTimeData = this.getAdditionalTimeLimitedData();
    if (_.has(additionalTimeData, 'calculated.duration')) {
      switch (additionalTimeData?.calculated.duration) {
      case 1:
        return `${additionalTimeData?.calculated.duration} mesec`;
      case 2:
      case 3:
      case 4:
        return `${additionalTimeData?.calculated.duration} meseca`;
      }
      return `${additionalTimeData?.calculated.duration} meseci`;
    }
    return '';
  }

  public renderAdditionalLimitedData(): string {
    let renderString = '';
    const dataDefaultBenefit = this.getDataBenefit(CampaignConstants.benefit.name.DEFAULT);
    if (
      dataDefaultBenefit &&
      _.has(dataDefaultBenefit, 'calculated') &&
      typeof dataDefaultBenefit.calculated === 'number'
    ) {
      renderString += ` + ${this.renderData(dataDefaultBenefit.calculated)}`;
    }

    const dataPromoBenefit = this.getDataBenefit(CampaignConstants.benefit.name.PROMO);
    if (dataPromoBenefit && _.has(dataPromoBenefit, 'calculated') && typeof dataPromoBenefit.calculated === 'number') {
      renderString += ` + ${this.renderData(dataPromoBenefit.calculated)}`;
    }

    return renderString;
  }

  public getLevel1Price(): number {
    if (this.getFinalPrice() !== this.subscription) {
      return this.subscription;
    }
    return 0;
  }

  public getLevel2Price(): number {
    const mfDiscountBasicBenefit = this.getMfDiscountBenefit(
      CampaignConstants.benefit.name.DEFAULT
    ) as ISoftOfferBenefit | null;
    if (mfDiscountBasicBenefit) {
      const level2Price = this.subscription - mfDiscountBasicBenefit.calculated;
      if (level2Price !== this.getFinalPrice()) {
        return level2Price;
      }
      return 0;
    }
    return 0;
  }

  public getFinalPrice(): number {
    let finalPrice = this.subscription;
    const mfDiscountBasicBenefit = this.getMfDiscountBenefit(
      CampaignConstants.benefit.name.DEFAULT
    ) as ISoftOfferBenefit | null;
    if (mfDiscountBasicBenefit) {
      finalPrice -= mfDiscountBasicBenefit.calculated;
    }

    const mfDiscountPromoBenefit = this.getMfDiscountBenefit(
      CampaignConstants.benefit.name.PROMO
    ) as ISoftOfferBenefit | null;
    if (mfDiscountPromoBenefit) {
      finalPrice -= mfDiscountPromoBenefit.calculated;
    }

    return finalPrice;
  }

  public getMfDiscountBenefit(
    benefitLevel: string = CampaignConstants.benefit.name.DEFAULT,
    benefitType: string = CampaignConstants.benefit.type.BASIC
  ): ISoftOfferBenefit | null {
    return this.getBenefit(CampaignConstants.benefit.category.MF_DISCOUNT, benefitLevel, benefitType);
  }

  public getDataBenefit(
    benefitLevel: string = CampaignConstants.benefit.name.DEFAULT,
    benefitType: string = CampaignConstants.benefit.type.BASIC
  ): ISoftOfferBenefit | null {
    return this.getBenefit(CampaignConstants.benefit.category.DATA, benefitLevel, benefitType);
  }

  public getBenefit(
    benefitCategory: string = CampaignConstants.benefit.category.MF_DISCOUNT,
    benefitLevel: string = CampaignConstants.benefit.name.DEFAULT,
    benefitType: string = CampaignConstants.benefit.type.BASIC
  ): ISoftOfferBenefit | null {
    switch (benefitType) {
    case CampaignConstants.benefit.type.BASIC:
      if (
        _.get(this.benefits, `${benefitCategory}.${benefitLevel}.type`, null) === CampaignConstants.benefit.type.BASIC
      ) {
        return _.get(this.benefits, `${benefitCategory}.${benefitLevel}`) as unknown as ISoftOfferBenefit;
      }
      return null;
    case CampaignConstants.benefit.type.COMPLEX:
      if (
        _.get(this.benefits, `${benefitCategory}.${benefitLevel}.type`, null) ===
        CampaignConstants.benefit.type.COMPLEX
      ) {
        return _.get(this.benefits, `${benefitCategory}.${benefitLevel}`) as unknown as ISoftOfferBenefit;
      }
      return null;
    }
    return null;
  }

  public getDsNumberForRender(): string | number {
    if (this.maxDsNumber) {
      return 'sve';
    }
    return this.dsNumber;
  }

  public hasSummerDataBenefit(): boolean {
    return this.data.unlimited && this.hasAdditionalLimitedData();
  }

  public renderSummerDataBenefit(): string | null {
    if (this.hasSummerDataBenefit()) {
      return `${this.renderDataValue()} ${this.renderAdditionalLimitedData()}`;
    }
    return null;
  }

  public static getUnlimitedTextForSpecType(type: SoftOfferSpecType): string {
    const labels = getLabelsByLanguage();
    let text = '';
    switch (type) {
    case 'data':
      text = labels.unlimitedData;
      break;
    case 'phone&sms':
      text = labels.unlimitedPhoneAndSMS;
      break;
    case 'phone':
      text = labels.unlimitedCalls;
      break;
    case 'sms':
      text = labels.unlimitedSMS;
      break;
    default:
      break;
    }
    return text;
  }

  public isApplicableDataBenefitBox(): boolean {
    return !this.isDataUnlimited() && (this.hasOnlyPromoDataBenefit() || this.hasBothBasicAndPromoDataBenefit());
  }

  public getDataBenefitBoxText(): string {
    const { duration, value, unit } = this.benefits.data.promoBenefit?.calculated || {};
    const { mbs } = this.data;

    if (value === 'UNLMTD') {
      return `+ Neograničen internet prvih ${duration} ${determineLabelForMonths(duration)}`;
    }

    const normalizedValue = produceValidMBValue(value, unit);
    const xTimes = (mbs + normalizedValue) / mbs;

    if (xTimes === 2) {
      return `Duplo više interneta u prvih ${duration} meseci uz ugovor na 24 meseca`;
    }

    if ([4, 5].includes(xTimes)) {
      return `${xTimes} puta više interneta u prvih ${duration} meseci uz ugovor na 24 meseca`;
    }

    return `${this.renderData(value * 1000)} više interneta u prvih ${duration} ${determineLabelForMonths(
      duration
    )} uz ugovor od 24 meseca`;
  }

  public isApplicableNetworkMinutes(): boolean {
    return Number(this.mint) > 0;
  }

  public getNetworkMinutesText(labels: any): string {
    return `${this.mint} ${labels.campaignMinutesInNetwork}`;
  }

  public isApplicableWestBalkansRoaming(): boolean {
    return Number(this.rzb?.mbs) > 0;
  }

  public getWestBalkansRoamingText(): string {
    return `${this.renderData(this.rzb.mbs)} u regionu Zapadnog Balkana`;
  }

  public getWestBalkansRoamingDescription(): string {
    return 'Količina interneta raspoloživa u regionu Zapadnog Balkana: Bosna i Hercegovina, Crna Gora, Severna Makedonija, Albanija i AP Kosovo i Metohija.';
  }

  public isApplicableLimitedFullSpeedData(): boolean {
    return Boolean(this.data.unlimited);
  }

  public getLimitedFullSpeedDataSlim(labels: any): any {
    if (this.benefits?.data?.promoBenefit) {
      const { unit, value } = this.benefits.data?.promoBenefit?.calculated || {};
      if (!+value) {
        return '-';
      }
      let mbsValue = this.data.mbs;

      if (unit === GB) {
        mbsValue += value * 1000;
      }

      if (unit === MB) {
        mbsValue += value;
      }

      if (this.data.unlimited) {
        return (
          <div className="text-center line-height-16 mb-2 text-14">
            <p className="mb-1">
              {labels.unlimited}
            </p>
            <p className="yettel-font"><span className="color-yettel-crossed-out-grey line-through mb-0 yettel-font">{this.renderData(this.data.mbs)}</span> {this.renderData(mbsValue)}</p>
            <p className="yettel-light-font">
              {labels.atFullSpeed}
            </p>
          </div>
        );
      } else if (!this.data.unlimited) {
        return (<div className="text-center line-height-16">
          <p className="yettel-font color-yettel-crossed-out-grey line-through"> {this.renderData(this.data.mbs)}</p>
          <p>{this.renderData(mbsValue)}</p>
        </div>);
      }

      return (
        <>
          {this.renderData(mbsValue)}
        </>
      );
    }

    if (this.data.unlimited) {
      return (
        <div className="text-center line-height-16 mb-2 text-14">
          <p className="mb-1">
            Neograničeno
          </p>
          <p className="yettel-font white-space-pre">
            {this.renderData(this.data.mbs)} {this.benefits?.data?.benefit?.type === CampaignConstants.benefit.type.BASIC && (<>+ {this.benefits?.data?.benefit.originalValue} {this.benefits.data?.benefit.originalUnit} </>)}
          </p>
          <p className="yettel-light-font">
            po punoj brzini
          </p>
        </div>
      );
    } else if (!this.data.unlimited) {
      return (<div className="text-center line-height-16">
        <p>{this.renderData(this.data?.mbs)} {this.benefits?.data?.benefit?.type === CampaignConstants.benefit.type.BASIC && (<>+ {this.benefits?.data?.benefit.originalValue} {this.benefits.data?.benefit.originalUnit} </>)}</p>
      </div>);
    }
  }

  public getLimitedFullSpeedData(): string {
    if (this.benefits?.data?.promoBenefit) {
      const { unit, value } = this.benefits.data?.promoBenefit?.calculated || {};
      let mbsValue = this.data.mbs;

      if (unit === GB) {
        mbsValue += value * 1000;
      }

      if (unit === MB) {
        mbsValue += value;
      }

      return this.renderData(mbsValue);
    }

    return this.renderData(this.data.mbs);
  }

  public getLimitedFullSpeedDataText(): string {
    if (this.benefits?.data?.promoBenefit) {
      const { unit, value, duration } = this.benefits?.data?.promoBenefit?.calculated || {};
      let mbsValue = this.data.mbs;

      if (unit === GB) {
        mbsValue += value * 1000;
      }

      if (unit === MB) {
        mbsValue += value;
      }

      return `<strong>${this.renderData(mbsValue)} po punoj brzini</strong> ${duration} meseci uz ugovor na 24 meseca`;
    }

    return `${this.renderData(this.data.mbs)} po punoj brzini`;
  }

  public getLimitedSpeedDataXmassCrossedOutText(): string {
    const { value, unit } = this.benefits?.data.promoBenefit?.calculated || {};
    const { mbs } = this.data;

    const normalizedValue = produceValidMBValue(value, unit);
    const xTimes = (mbs + normalizedValue) / mbs;

    if ([2, 4, 5].includes(xTimes)) {
      return '';
    }
    if (this.benefits.data?.promoBenefit) {
      return this.renderData(this.data.mbs);
    }

    return '';
  }

  public getLimitedFullSpeedDataCrossedOutText(): string {
    if (this.benefits?.data?.promoBenefit) {
      return this.renderData(this.data.mbs);
    }

    return '';
  }

  public isApplicableDataServicesList(): boolean {
    return false;
    // return this.dsNumber > 0;
  }

  public getDataServicesText(): string {
    return `${this.getDsNumberForRender()} ${[2, 3, 4, "sve"].includes(this.getDsNumberForRender()) ? 'aplikacije' : 'aplikacija'} iz ponude`;
  }

  public hasBasicDataBenefit(): boolean {
    return Number(this.benefits?.data?.benefit?.calculated) > 0;
  }

  public hasPromoDataBenefit(): boolean {
    return Number(this.benefits?.data?.promoBenefit?.calculated?.duration) > 0;
  }

  public hasOnlyBasicDataBenefit(): boolean {
    return this.hasBasicDataBenefit() && !this.hasPromoDataBenefit();
  }

  public hasOnlyPromoDataBenefit(): boolean {
    return !this.hasBasicDataBenefit() && this.hasPromoDataBenefit();
  }

  public hasBothBasicAndPromoDataBenefit(): boolean {
    return this.hasBasicDataBenefit() && this.hasPromoDataBenefit();
  }

  public buildOfferSpecProp(
    type: SoftOfferSpecType,
    isUnlimited: boolean,
    value?: string,
    isLinethrough?: boolean,
    oldValue?: string
  ): OfferSpecProps {
    return {
      type,
      isUnlimited,
      value: isUnlimited ? CampaignConstants.unlimitedIdentifier : value,
      isLinethrough,
      oldValue,
    };
  }

  public getOfferMainSpecs(): OfferSpecProps[] {
    const result: OfferSpecProps[] = [];

    if (this.isDataUnlimited()) {
      result.push(this.buildOfferSpecProp('data', true));
    } else {
      const dataInfo: OfferSpecProps = this.buildOfferSpecProp('data', false, this.renderDataValue());

      if (this.hasOnlyBasicDataBenefit()) {
        dataInfo.isLinethrough = true;
        dataInfo.oldValue = this.renderDataValue();
        dataInfo.value = this.renderData(this.benefits.data?.benefit?.calculated + this.data.mbs);
      }

      if (this.hasOnlyPromoDataBenefit()) {
        if (this.benefits.data.promoBenefit?.calculated.value !== 'UNLMTD') {
          const { value, unit } = this.benefits.data.promoBenefit?.calculated || {};
          dataInfo.isLinethrough = true;
          dataInfo.oldValue = this.renderDataValue();
          dataInfo.value = this.renderData(produceValidMBValue(value, unit) + this.data.mbs);
        }
      }

      if (this.hasBothBasicAndPromoDataBenefit()) {
        const { value, unit } = this.benefits.data.promoBenefit?.calculated || {};

        dataInfo.isLinethrough = true;
        dataInfo.oldValue = this.renderDataValue();
        if (this.benefits.data.promoBenefit?.calculated.value !== 'UNLMTD') {
          const normalizedValue = produceValidMBValue(value, unit);
          dataInfo.value = this.renderData(normalizedValue + this.data.mbs);
          dataInfo.hasAddedValue = true;
          dataInfo.addedValue = ` + ${this.renderData(produceValidMBValue(value, unit))}`;
        }

        if (this.benefits?.data?.benefit?.type === 'basic') {
          dataInfo.value = this.renderData(
            produceValidMBValue(this.benefits?.data?.benefit?.calculated, MB) + this.data.mbs
          );
        }
      }

      result.push(dataInfo);
    }

    if (this.isUnlimitedAll()) {
      result.push(this.buildOfferSpecProp('phone&sms', true));
      return result;
    }

    result.push(this.buildOfferSpecProp('phone', this.isMinUnlimited(), `${this.min} min`));
    result.push(this.buildOfferSpecProp('sms', this.isSmsUnlimited(), `${this.sms} SMS`));

    return result;
  }

  public isApplicableMonthlyFeeBenefit(): boolean {
    return Boolean(this.benefits.mfDiscount?.benefit);
  }

  public getMonthlyFeeBenefitValue(): number {
    return this.benefits.mfDiscount.benefit?.calculated;
  }

  public getMonthlyFeeBenefitText(labels: any, isShort?: boolean): string {
    return `${this.benefits.mfDiscount.benefit?.originalValue} ${this.benefits.mfDiscount.benefit?.originalUnit} ${isShort ? labels.ofDiscount : labels.ofSubscriptionDiscount}`;
  }

}
